import React, { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getActivePackagesByRoom, getCalendarsToBeFetchedByRoom, getMultiRoomPackageTotals, isLoadingByRoom, LoadingStatusByRoom, MultiRoomPackageTotals, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import MultiRoomOfferSelectRoomsButton from '../MultiRoomOfferSelectRoomsButton'
import MultiRoomOfferAddToCartButton from '../MultiRoomOfferAddToCartButton'
import MultiRoomOfferBookButton from '../MultiRoomOfferBookButton'
import MobileMultipleRoomsDurationLabel from './MobileMultipleRoomsDurationLabel'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { EmptyArray, groupBy, nonNullable } from 'lib/array/arrayUtils'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import TextButton from 'LuxKit/Button/TextButton'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms } from 'components/OfferPage/BnblModal/utils'
import { useAppDispatch } from 'hooks/reduxHooks'
import getDatasetFromEvent from 'lib/analytics/getDatasetFromEvent'
import OfferPageBNBLcta from 'components/OfferPage/OfferPageBNBLcta'
import config from 'constants/config'

const FooterContainer = styled(Pane)`
  padding: ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  rooms: Array<App.RoomOccupants>;
  checkIn?: string;
  isFlightsSelected: boolean;
  multiItemCartEnabled: boolean;
  multiRoomPackages: PackageOptionViewByRoom;
  multiRoomPackageTotals: MultiRoomPackageTotals;
  isLoadingByRoom: LoadingStatusByRoom;
  calendarsToBeFetchedByRoom: LoadingStatusByRoom;
  canRedeemLuxPlusBenefits: boolean;
  baseBnblPackageViews: Array<OfferPackageOptionView>;
}

function MobileMultipleRoomsIndividualRoomsFooter(props: Props) {
  const { offer, rooms, checkIn, isFlightsSelected, multiItemCartEnabled, multiRoomPackages, multiRoomPackageTotals, isLoadingByRoom, calendarsToBeFetchedByRoom, canRedeemLuxPlusBenefits, baseBnblPackageViews } = props

  const {
    hotelPrice,
    hotelMemberPrice,
    hotelPlusFlightsPrice,
    hotelPlusFlightsMemberPrice,
    flightPrice,
  } = multiRoomPackageTotals

  const currentContact = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const shouldPreventMultipleRoomsBooking = rooms.length > 2 && canRedeemLuxPlusBenefits && hotelMemberPrice > 0
  const isCapacityValid = Object.values(multiRoomPackages).every(pkg => pkg?.capacityValid)
  const isAllowBuyNowBookLater = Object.values(multiRoomPackages).every(pkg => pkg?.package.allowBuyNowBookLater)
  const showBNBL = offer.hasBuyNowBookLater && config.accommodation.hasBnbl && isCapacityValid && isAllowBuyNowBookLater

  const isBookNowDisabled = useMemo(() => (
    Object.values(isLoadingByRoom).some(Boolean) ||
    Object.values(calendarsToBeFetchedByRoom).some(Boolean) ||
    Object.values(multiRoomPackages).some(pkg => !pkg)
  ), [isLoadingByRoom, calendarsToBeFetchedByRoom, multiRoomPackages])

  const loyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(multiRoomPackages)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView })
    ))
  }, [offer, multiRoomPackages])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...loyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice,
      skip: !isFlightsSelected,
    }),
  ], [offer, isFlightsSelected, flightPrice, loyaltyHotelOptions])

  const onOpenBnblModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    offerPageDispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })

    const isGift = getDatasetFromEvent(e, 'bnbltype') === 'gift'
    const selectedPackages = nonNullable(Object.values(multiRoomPackages))
    const groupedPackagesRecords = groupBy(selectedPackages, pkg => pkg.package.uniqueKey)
    const packages = [...groupedPackagesRecords.entries()].map(groupedPackage => {
      const basePackage = baseBnblPackageViews.find(packageView => packageView.package.uniqueKey === groupedPackage[0])!
      const numberOfRooms = groupedPackage[1].length

      return {
        packageView: getBnblPackageViewByNumberOfRooms(basePackage, numberOfRooms),
        numberOfRooms,
      }
    })

    dispatch(initialiseBnblOfferModal(packages, offer.id, isGift))
  }, [dispatch, offerPageDispatch, multiRoomPackages, offer.id, baseBnblPackageViews])

  return (
    <FooterContainer>
      <Group direction="vertical" gap={20}>
        {hotelPrice > 0 && <>
          <div>
            <PriceRowPriceCaption>
              <MobileMultipleRoomsDurationLabel shouldDisplayRoom />
            </PriceRowPriceCaption>
            <LuxPlusPriceStack
              size="S"
              price={isFlightsSelected ? hotelPlusFlightsPrice : hotelPrice}
              memberPrice={isFlightsSelected ? hotelPlusFlightsMemberPrice : hotelMemberPrice}
              saleUnit={isFlightsSelected && !hasFlights ? 'total*' : 'total'}
              testid="pricing"
              offerConfig={offer.luxPlus}
            />
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
          />
        </>}
        {!shouldPreventMultipleRoomsBooking && <>
          {isFlightsSelected && <MultiRoomOfferSelectRoomsButton size="large" fit="flex" disabled={isBookNowDisabled} />}
          {!isFlightsSelected && <Group direction="horizontal" gap={12} horizontalAlign="stretch">
            {multiItemCartEnabled && checkIn && <MultiRoomOfferAddToCartButton size="large" fit="flex" disabled={isBookNowDisabled} />}
            <MultiRoomOfferBookButton size="large" fit="flex" disabled={isBookNowDisabled} />
          </Group>}
          {showBNBL && <OfferPageBNBLcta onOpenModal={onOpenBnblModal} direction="horizontal" />}
        </>}
        {shouldPreventMultipleRoomsBooking && (
          <Sheet background="neutral-seven" padding={20}>
            <Group direction="vertical" gap={8}>
              <Group direction="vertical" gap={4}>
                <BodyText variant="medium" weight="bold">Ready to book? Call us to complete your booking</BodyText>
                <BodyText variant="medium">
                  Our team are available {NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? '7 days a week from 9am to 7pm' : '24 hours, 7 days'}
                </BodyText>
              </Group>
              <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${currentContact.defaultContact.number}`} fit="flex">
                Call {currentContact.defaultContact.humanReadable}
              </TextButton>
            </Group>
          </Sheet>
        )}
      </Group>
    </FooterContainer>
  )
}

type MappedProps = Pick<Props,
  'offer'
  | 'rooms'
  | 'checkIn'
  | 'isFlightsSelected'
  | 'multiItemCartEnabled'
  | 'multiRoomPackages'
  | 'multiRoomPackageTotals'
  | 'isLoadingByRoom'
  | 'calendarsToBeFetchedByRoom'
  | 'canRedeemLuxPlusBenefits'
  | 'baseBnblPackageViews'
>
export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  checkIn: state.checkInDate,
  isFlightsSelected: state.flights,
  multiRoomPackages: getActivePackagesByRoom(state, appState),
  multiRoomPackageTotals: getMultiRoomPackageTotals(state, appState),
  multiItemCartEnabled: isMultiCartEnabled(appState),
  isLoadingByRoom: isLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
}), MobileMultipleRoomsIndividualRoomsFooter)
